<template>
  <Messenger
    :title="t('components.messenger.contact.title')"
    :description="t('components.messenger.contact.description')"
    :back-link="route('space.problems', [tenant.slug, problem.space.id])"
  >
    <template v-slot:content>
      <div class="flex flex-col h-full">
        <div class="grow p-4 flex flex-col">
          <p class="text-sm font-medium text-gray-500 pb-4">
            {{ t("components.messenger.contact.add_details") }}
          </p>

          <Errors />

          <div v-if="requireName">
            <label for="email" class="block text-sm font-medium text-gray-700">
              {{ t("validation.attributes.name") }}</label
            >
            <div class="mt-1">
              <input
                v-model="details.name"
                type="text"
                name="name"
                id="name"
                class="block w-full rounded-sm border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
              />
            </div>
          </div>

          <div class="flex items-center my-4" v-if="requireName">
            <div class="flex-grow h-px bg-gray-300"></div>
          </div>

          <div
            v-if="
              requiredFields === 'only_email' ||
              requiredFields === 'email_or_phone'
            "
          >
            <label for="email" class="block text-sm font-medium text-gray-700">
              {{ t("validation.attributes.email") }}
            </label>
            <div class="mt-1">
              <input
                v-model="details.email"
                type="email"
                name="email"
                id="email"
                class="block w-full rounded-sm border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
              />
            </div>
          </div>

          <div
            class="flex items-center my-2"
            v-if="requiredFields === 'email_or_phone'"
          >
            <div class="flex-grow h-px bg-gray-300"></div>
            <span
              class="flex-shrink text-gray-500 px-4 italic font-light lowercase"
              >{{ t("components.or") }}</span
            >
            <div class="flex-grow h-px bg-gray-300"></div>
          </div>

          <div
            v-if="
              requiredFields === 'only_phone' ||
              requiredFields === 'email_or_phone'
            "
          >
            <label
              for="phone"
              class="mb-3 block text-sm font-medium text-gray-700"
            >
              {{ t("validation.attributes.phone") }}
            </label>
            <phone-input
              defaultCountry="BE"
              :value="`${details.phone.dialCode}${details.phone.nationalNumber}`"
              :allowed="[]"
              name="phone"
              required
              @phoneData="handlePhoneEntered"
            />
          </div>
        </div>
        <div class="border-t border-gray-200 p-4 flex gap-4">
          <div class="flex grow justify-between text-xs text-gray-500">
            <i18n-t keypath="components.terms_and_conditions.message" tag="p">
              <template v-slot:label>
                <span
                  v-text="t('components.buttons.send')"
                  class="text-lowercase"
                />
              </template>
              <template v-slot:terms>
                <a
                  href="https://www.fixform.com/terms-of-use"
                  target="_blank"
                  v-text="t('components.terms_and_conditions.terms_label')"
                  class="underline hover:text-gray-900"
                />
              </template>
              <template v-slot:privacy>
                <a
                  href="https://www.fixform.com/privacy-policy"
                  target="_blank"
                  v-text="t('components.terms_and_conditions.privacy_label')"
                  class="underline hover:text-gray-900"
                />
              </template>
            </i18n-t>
          </div>

          <div>
            <primary-button
              :label="t('components.buttons.send')"
              :loading="isLoading"
              @click="handleSubmitContactDetails"
              :disabled="disableSend"
            />
          </div>
        </div>
      </div>
    </template>
  </Messenger>
</template>

<script setup>
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton.vue";
import { computed, ref } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import Messenger from "@/Tailwind/Layouts/Messenger.vue";
import { PhoneInput } from "@lbgm/phone-number-input";
import "@lbgm/phone-number-input/style";

import { useI18n } from "vue-i18n";
import Errors from "@/Tailwind/Components/Errors.vue";
import { useStorage } from "@vueuse/core";

const { t } = useI18n();

const details = useStorage("messenger", {
  email: null,
  phone: {
    country: null,
    dialCode: null,
    nationalNumber: null,
    number: null,
  },
  name: null,
});

const isLoading = ref(false);

const tenant = usePage().props.tenant;
const problem = usePage().props.problem;
const messenger = usePage().props.problem.messenger;
const requiredFields = usePage().props.tenant.settings.notification_options;
const requireName = usePage().props.tenant.settings.require_name;

// Report type
const isReport = usePage().props.input.report;
const isUpvote = usePage().props.input.upvote;

const disableSend = computed(() => {
  if (requireName && !details.value.name) {
    return true;
  }

  switch (requiredFields) {
    case "only_email":
      return !details.value.email;
    case "only_phone":
      return !details.value.phone;
    case "email_or_phone":
      return !details.value.email && !details.value.phone;
    default:
      return false;
  }
});

function handlePhoneEntered(data) {
  details.value.phone = {
    country: data.country,
    dialCode: data.dialCode,
    nationalNumber: data.nationalNumber,
    number: data.number,
  };
}

function handleSubmitContactDetails() {
  router.post(
    route("problem.report.contact.store", [tenant, problem, messenger]),
    {
      report: isReport,
      upvote: isUpvote,
      email: details.value.email,
      mobile: details.value.phone.number,
      country_code: details.value.phone.country,
      name: details.value.name,
    },
    {
      onStart: () => (isLoading.value = true),
      onFinish: () => (isLoading.value = false),
    }
  );
}
</script>

<style lang="scss">
.baseinput-core {
  @apply bg-white w-full rounded-sm border border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm #{!important};

  input {
    @apply border-transparent focus:border-transparent focus:ring-0 text-sm;
  }
}

.lbgm-phone-scrll div {
  @apply hover:bg-gray-50;
}
</style>
